import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  MessageSquare, Pin, Lock, CheckCircle2, ThumbsUp, Search, Plus, X, Tag,
  Filter, ShieldCheck, Paperclip,
} from "lucide-react";
import MarkdownEditor from "@/components/MarkdownEditor";

function fmtRel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "hace unos segundos";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `hace ${Math.floor(diff / 86400)} d`;
  return d.toLocaleDateString("es-MX", { day: "numeric", month: "short" });
}

const SORTS = [
  { value: "recent", label: "Recientes" },
  { value: "popular", label: "Populares" },
  { value: "pinned", label: "Fijados primero" },
];

const ROLE_BADGE = {
  instructor: "bg-purple-500/15 text-purple-200 border-purple-500/40",
  staff_dcc: "bg-brand-orange-500/15 text-white/70 border-primary/40",
  superadmin_dcc: "bg-brand-orange-500/15 text-white/70 border-primary/40",
  alumno: "bg-gray-500/15 text-gray-300 border-gray-500/40",
};

const ROLE_LABEL = {
  instructor: "Instructor",
  staff_dcc: "Staff",
  superadmin_dcc: "Superadmin",
  alumno: "Alumno",
  partner_admin: "Partner",
};

/**
 * Componente autocontenido de foro por programa. Se puede usar como tab
 * dentro de `ProgramaView` o como página independiente. Recibe `programSlug`
 * y `basePath` para construir links a threads (default `./foro/thread/:id`).
 */
export default function ProgramForum({ programSlug, basePath = "" }) {
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [threads, setThreads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("recent");
  const [tag, setTag] = useState("");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [forbidden, setForbidden] = useState(null);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("sort", sort);
    if (tag) params.set("tag", tag);
    if (q) params.set("q", q);
    params.set("limit", "20");
    api.get(`/forums/${programSlug}/threads?${params.toString()}`)
      .then((r) => { setThreads(r.data.items); setTotal(r.data.total); setForbidden(null); })
      .catch((e) => {
        if (e.response?.status === 403) setForbidden(e.response.data?.detail?.reason || "needs_program_access");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get(`/forums/${programSlug}`)
      .then((r) => setInfo(r.data))
      .catch((e) => { if (e.response?.status === 403) setForbidden("needs_program_access"); });
  }, [programSlug]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [programSlug, sort, tag]);

  if (forbidden === "needs_program_access") {
    return (
      <div className="p-8 border border-dashed border-gray-800 rounded-xl text-center" data-testid="forum-locked">
        <MessageSquare className="w-8 h-8 mx-auto text-gray-500" />
        <p className="mt-3 text-white font-semibold">El foro es solo para alumnos inscritos</p>
        <p className="mt-1 text-sm text-gray-400">Necesitas una licencia, comprar el programa o tener All-Access activo.</p>
        <Link to="/planes" className="mt-4 inline-block text-sm font-semibold text-[#93C5FD] hover:text-[#3B82F6] underline underline-offset-2">
          Ver planes All-Access →
        </Link>
      </div>
    );
  }

  return (
    <div>
      <header className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#93C5FD] font-semibold flex items-center gap-1">
            <MessageSquare className="w-3.5 h-3.5" /> Foro de la comunidad
          </p>
          <h2 className="mt-1 text-xl font-bold text-white">{info?.forum?.program_title || "Cargando…"}</h2>
          <p className="mt-1 text-xs text-gray-500 font-mono">
            {info?.forum ? `${info.forum.threads_count} temas · ${info.forum.posts_count} respuestas` : ""}
            {info?.my_permissions?.is_moderator && (
              <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-500/15 border border-purple-500/40 text-purple-200 text-[10px] uppercase tracking-widest">
                <ShieldCheck className="w-3 h-3" /> moderador
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/app/foros/buscar?program_slug=${programSlug}`}
            data-testid="forum-search-btn"
            className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-gray-700 text-gray-300 hover:bg-white/5 text-xs font-semibold"
          >
            <Search className="w-3.5 h-3.5" /> Buscar
          </Link>
          {info?.my_permissions?.can_write && (
            <button
              type="button"
              onClick={() => setCreating(true)}
              data-testid="new-thread-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#B84A0C] hover:bg-primary text-white text-sm font-semibold"
            >
              <Plus className="w-4 h-4" /> Nuevo tema
            </button>
          )}
        </div>
      </header>

      <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="forum-filters">
        <div className="flex-1 min-w-64">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(); }}
              placeholder="Buscar en temas…"
              data-testid="forum-search-input"
              className="w-full bg-[#0F172A] border border-gray-800 rounded-md pl-9 pr-3 py-2 text-sm text-white focus:outline-none focus:border-[#3B82F6]"
            />
          </div>
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 flex items-center gap-1">
            <Filter className="w-3 h-3" /> Ordenar
          </label>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            data-testid="forum-sort-select"
            className="mt-1 bg-[#0F172A] border border-gray-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#3B82F6]"
          >
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        {tag && (
          <button
            type="button"
            onClick={() => setTag("")}
            data-testid="forum-clear-tag"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#3B82F6]/15 border border-[#3B82F6]/40 text-[#93C5FD] text-xs"
          >
            <Tag className="w-3 h-3" /> {tag} <X className="w-3 h-3" />
          </button>
        )}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 bg-[#111827] border border-gray-800 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : threads.length === 0 ? (
        <div className="p-8 border border-dashed border-gray-800 rounded-xl text-center" data-testid="forum-empty">
          <p className="text-white font-semibold">Aún no hay temas en este foro</p>
          <p className="mt-1 text-sm text-gray-400">Sé el primero en abrir la conversación.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="threads-list">
          {threads.map((t) => (
            <ThreadRow
              key={t.id}
              thread={t}
              basePath={basePath}
              onTagClick={setTag}
            />
          ))}
        </div>
      )}
      <p className="mt-3 text-xs text-gray-500 font-mono text-right">{total} temas</p>

      {creating && (
        <NewThreadModal
          programSlug={programSlug}
          onClose={() => setCreating(false)}
          onCreated={(t) => {
            setCreating(false);
            toast.success("Tema publicado");
            nav(`${basePath}/foro/thread/${t.id}`);
          }}
        />
      )}
    </div>
  );
}

function ThreadRow({ thread, basePath, onTagClick }) {
  const t = thread;
  const roleBadge = ROLE_BADGE[t.author_role] || ROLE_BADGE.alumno;
  const roleLabel = ROLE_LABEL[t.author_role] || t.author_role;
  return (
    <Link
      to={`${basePath}/foro/thread/${t.id}`}
      data-testid={`thread-row-${t.id}`}
      className="block p-4 bg-[#111827] border border-gray-800 rounded-xl hover:border-[#3B82F6]/60 transition-colors group"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {t.is_pinned && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[10px] font-bold uppercase tracking-widest">
                <Pin className="w-3 h-3" /> Fijado
              </span>
            )}
            {t.is_locked && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-500/15 border border-gray-500/40 text-gray-300 text-[10px] font-bold uppercase tracking-widest">
                <Lock className="w-3 h-3" /> Bloqueado
              </span>
            )}
            {t.has_solution && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[10px] font-bold uppercase tracking-widest">
                <CheckCircle2 className="w-3 h-3" /> Resuelto
              </span>
            )}
            {t.status === "hidden" && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-500/15 border border-red-500/40 text-red-300 text-[10px] font-bold uppercase tracking-widest">
                Oculto
              </span>
            )}
            {t.status === "reported" && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-500/15 border border-amber-500/40 text-amber-300 text-[10px] font-bold uppercase tracking-widest">
                Reportado
              </span>
            )}
          </div>
          <h3 className="mt-2 text-base font-semibold text-white group-hover:text-[#93C5FD] break-words">
            {t.title}
          </h3>
          <p className="mt-1 text-xs text-gray-500 line-clamp-2">
            {(t.body_md || "").replace(/[*_`>#\-\[\]()]/g, "").slice(0, 180)}
          </p>
          <div className="mt-3 flex items-center gap-2 flex-wrap text-[11px]">
            <span className="text-gray-400">{t.author_name}</span>
            <span className={`px-1.5 py-0.5 rounded border ${roleBadge} uppercase tracking-widest font-semibold`}>
              {roleLabel}
            </span>
            <span className="text-gray-500 font-mono">· {fmtRel(t.created_at)}</span>
            {(t.tags || []).map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onTagClick?.(tag); }}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/5 border border-gray-700 text-gray-300 hover:text-white hover:bg-white/10"
              >
                <Tag className="w-2.5 h-2.5" /> {tag}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 text-[11px] text-gray-500 font-mono flex-shrink-0">
          <span className="inline-flex items-center gap-1">
            <MessageSquare className="w-3 h-3" /> {t.replies_count}
          </span>
          <span className="inline-flex items-center gap-1">
            <ThumbsUp className="w-3 h-3" /> {t.likes_count}
          </span>
          <span className="mt-1 text-[10px]">últ. {fmtRel(t.last_reply_at)}</span>
        </div>
      </div>
    </Link>
  );
}

function NewThreadModal({ programSlug, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [tags, setTags] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (title.trim().length < 20) { toast.error("Título mínimo 20 caracteres"); return; }
    if (body.trim().length < 30) { toast.error("Cuerpo mínimo 30 caracteres"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/forums/${programSlug}/threads`, {
        title, body_md: body,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        attachments: attachments.map((a) => a.id),
      });
      onCreated(r.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        data-testid="new-thread-form"
        className="w-full max-w-2xl bg-[#111827] border border-gray-800 rounded-xl p-6 space-y-4 max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#93C5FD]">Nuevo tema</p>
            <h3 className="text-xl font-semibold text-white mt-1">Abre una discusión</h3>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
            Título · min 20 · <span className="font-mono text-gray-500">{title.length}/200</span>
          </label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            data-testid="new-thread-title"
            placeholder="Ej. ¿Cómo estructurar el primer contacto con un prospecto UHNW?"
            className="w-full mt-1 bg-[#0F172A] border border-gray-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#3B82F6]"
          />
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-1 block">
            Cuerpo · min 30 caracteres · markdown · imágenes · @menciones
          </label>
          <MarkdownEditor
            value={body}
            onChange={setBody}
            minLength={30}
            rows={8}
            placeholder="Describe tu pregunta o aporte con detalle. Escribe @ para mencionar…"
            testid="new-thread-body"
            attachments={attachments}
            onAttachmentsChange={setAttachments}
            mentionProgramSlug={programSlug}
          />
          {attachments.length > 0 && (
            <p className="mt-1 text-[10px] text-gray-500 font-mono flex items-center gap-1">
              <Paperclip className="w-3 h-3" /> {attachments.length} imagen{attachments.length === 1 ? "" : "es"} adjunta{attachments.length === 1 ? "" : "s"}
            </p>
          )}
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
            Tags (separados por coma · opcional)
          </label>
          <input
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="duda, networking, recurso"
            data-testid="new-thread-tags"
            className="w-full mt-1 bg-[#0F172A] border border-gray-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#3B82F6]"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-gray-700 text-gray-300 hover:bg-white/5 text-sm">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="new-thread-submit"
            className="px-6 py-2 rounded-md bg-[#B84A0C] hover:bg-primary text-white text-sm font-semibold disabled:opacity-60"
          >
            {saving ? "Publicando…" : "Publicar tema"}
          </button>
        </div>
      </form>
    </div>
  );
}
